import React, { useState, useEffect, useMemo } from 'react';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, Switch, Select, Typography } from 'antd';
import { axiosInstance, API_BASE_URL } from '../../../providers/dataProvider';


export const LlmPromptCreate: React.FC = () => {
  const { formProps, saveButtonProps } = useForm({ resource: 'llm-prompts' });
  const [versionConfig, setVersionConfig] = useState<any>({});
  const [providers, setProviders] = useState<any[]>([]);

  useEffect(() => {
    axiosInstance.get(`${API_BASE_URL}/admin/llm/providers`)
      .then((resp) => {
        const data = Array.isArray(resp.data) ? resp.data : (resp.data?.data ?? resp.data?.items ?? []);
        setProviders(data.filter((p: any) => p.isActive));
      })
      .catch(() => {});
  }, []);

  const selectedProvider = providers.find((p: any) => p.id === versionConfig?.providerId);
  const modelOptions: string[] = useMemo(() => {
    const base: string[] = [];
    if (selectedProvider) {
      const list = selectedProvider.modelList;
      if (Array.isArray(list) && list.length > 0) base.push(...list);
      else if (selectedProvider.defaultModel) base.push(selectedProvider.defaultModel);
    }
    const savedModel = versionConfig?.model;
    if (savedModel && !base.includes(savedModel)) base.push(savedModel);
    return base;
  }, [selectedProvider, versionConfig?.model]);

  const enhancedOnFinish = async (values: any) => {
    return { ...values, config: versionConfig };
  };

  return (
    <Create resource="llm-prompts" saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical" onFinish={async (values) => {
        const enhanced = await enhancedOnFinish(values);
        formProps.onFinish?.(enhanced);
      }}>
        <Form.Item label="Name" name="name" rules={[{ required: true }]}>
          <Input placeholder="Prompt name" />
        </Form.Item>
        <Form.Item label="Slug" name="slug" rules={[{ required: true }]}>
          <Input placeholder="unique-slug-identifier" />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} placeholder="Describe what this prompt does" />
        </Form.Item>
        <Form.Item label="Content" name="content" rules={[{ required: true }]}>
          <Input.TextArea rows={15} placeholder="Enter prompt template content. Use {{variable}} for template variables." />
        </Form.Item>
        <Form.Item label="Change Note" name="changeNote">
          <Input placeholder="Initial version" />
        </Form.Item>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Provider</Typography.Text>
            <Select
              value={versionConfig?.providerId || undefined}
              onChange={(v) => setVersionConfig((c: any) => {
                const next = { ...c, providerId: v };
                if (v !== c?.providerId) delete next.model;
                return next;
              })}
              allowClear
              placeholder="Default (highest priority)"
              style={{ width: '100%' }}
              options={providers.map((p: any) => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Model</Typography.Text>
            <Select
              value={versionConfig?.model || undefined}
              onChange={(v) => setVersionConfig((c: any) => ({ ...c, model: v }))}
              allowClear
              placeholder={selectedProvider ? selectedProvider.defaultModel : 'Provider default'}
              style={{ width: '100%' }}
              options={modelOptions.map((m: string) => ({ value: m, label: m }))}
            />
          </div>
        </div>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Switch checked={!!versionConfig?.enableThinking}
            onChange={(v) => setVersionConfig((c: any) => ({ ...c, enableThinking: v }))} />
          <div>
            <Typography.Text strong style={{ fontSize: 13 }}>Enable Thinking</Typography.Text>
            <br />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Model reasons before answering. Increases latency but may improve accuracy.
            </Typography.Text>
          </div>
        </div>
      </Form>
    </Create>
  );
};
